// /a/:altId — the geo quest entry. M0 scope: prove the data path and show the
// checkpoint list; guided geo AR placement arrives in M1 (plan §4).
import {useEffect, useState} from 'react'
import type {RawAlternative, RawAnchor} from '../api/types'
import {fetchAlternative, fetchAnchors} from '../api/client'
import {assetUrl, isGeoLocated, num, isEnvironmentScan} from '../scene/mapping'

interface Props {
  alternativeId: string
  /** Optional context line, e.g. why the user landed in preview mode. */
  note?: string
}

export function QuestLanding({alternativeId, note}: Props) {
  const [alt, setAlt] = useState<RawAlternative | null>(null)
  const [anchors, setAnchors] = useState<RawAnchor[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    Promise.all([fetchAlternative(alternativeId), fetchAnchors(alternativeId)])
      .then(([a, list]) => {
        if (cancelled) return
        setAlt(a)
        setAnchors([...list].sort((x, y) => (num(x.order) ?? 0) - (num(y.order) ?? 0)))
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
    return () => {
      cancelled = true
    }
  }, [alternativeId])

  if (error)
    return (
      <div className="center-note">
        <p>Couldn't load this alternative.</p>
        <p className="dim">{error}</p>
      </div>
    )
  if (!alt) return <div className="center-note">Loading…</div>

  const cover = assetUrl(alt.main_image_asset?.path)
  return (
    <div className="landing">
      {cover && <img className="cover" src={cover} alt="" />}
      <div className="landing-body">
        <p className="eyebrow">AR experience</p>
        <h1>{alt.public_name || alt.name}</h1>
        {alt.description && <p className="dim">{alt.description}</p>}
        <div className="notice">
          {note ? (
            <>
              <strong>Preview mode.</strong> {note}
            </>
          ) : (
            <>
              <strong>Preview mode.</strong> Open any checkpoint below in AR from wherever
              you are — or start the guided on-location experience from the main page.
            </>
          )}
        </div>
        <ul className="anchor-list">
          {anchors.map((a) => (
            <li key={a.id}>
              <a className="anchor-row" href={`/c/${alternativeId}/${a.id}`}>
                <span>
                  {num(a.order) ?? '·'}. {a.name || `Checkpoint ${a.id}`}
                </span>
                <span className="dim">
                  {isGeoLocated(a.lat, a.lng) ? 'on location' : 'anywhere'} ·{' '}
                  {(a.anchor_assets ?? []).filter((x) => !isEnvironmentScan(x)).length} assets →
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
      <footer className="attribution">AR engine © 2026 Niantic Spatial, Inc.</footer>
    </div>
  )
}
